<template>
  <div class="vomi-login-page">
    <div class="vomi-login-card">
      <div class="vomi-logo">
        <div class="vomi-logo-circle">
          <img src="/logo.png" alt="Vomi" />
        </div>
      </div>
      <h1 class="vomi-title">{{ t.nativeLogin.title }}</h1>
      <form class="vomi-form" @submit.prevent="handleSubmit">
        <div class="vomi-field">
          <label for="native-login-email">{{ t.nativeLogin.emailLabel }}</label>
          <InputText
            id="native-login-email"
            v-model="email"
            type="email"
            autocomplete="username"
            autofocus
            :disabled="isBusy"
            class="w-full"
          />
        </div>
        <div class="vomi-field">
          <label for="native-login-password">{{ t.nativeLogin.passwordLabel }}</label>
          <Password
            input-id="native-login-password"
            v-model="password"
            :feedback="false"
            toggle-mask
            autocomplete="current-password"
            :disabled="isBusy"
            input-class="w-full"
            class="w-full"
          />
        </div>
        <small v-if="errorMessage" class="vomi-error" data-testid="native-login-error">
          {{ errorMessage }}
        </small>
        <Button
          type="submit"
          data-testid="native-login-submit"
          :loading="isBusy"
          :disabled="!canSubmit"
          :label="t.nativeLogin.submit"
          class="vomi-submit w-full"
        />
      </form>
      <button
        type="button"
        class="vomi-browser-fallback"
        data-testid="native-login-browser-fallback"
        :disabled="isBusy"
        @click="handleBrowserFallback"
      >
        {{ t.nativeLogin.browserFallback }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "@/locales/useI18n";
import { loginWithCredentials, login } from "@/security/keycloak";
import { DirectGrantError } from "@/security/nativeAuth";

// Mounted as its own tiny Vue+PrimeVue app by main.ts, before the real app (Router/Pinia) exists —
// see showNativeLoginScreen(). onSuccess is a plain callback prop, not an emit, so the caller can
// unmount this instance and call startApp() from outside any component context.
const props = defineProps<{ onSuccess: () => void }>();

const { t: tRef } = useI18n();
const t = computed(() => tRef.value);

const email = ref("");
const password = ref("");
const isBusy = ref(false);
const errorMessage = ref("");

const canSubmit = computed(() => email.value.trim() !== "" && password.value !== "" && !isBusy.value);

// Best-effort: Keycloak's conditional-OTP direct-grant sub-flow (see keycloak-data/b2b-realm.json,
// "Direct Grant - Conditional OTP") rejects with invalid_grant same as a wrong password, distinguishable
// only by the description text — no B2B user has TOTP configured today, so this is untested against a
// live rejection. Wrong detection just falls back to the generic message; the browser-login link below
// stays visible regardless and handles OTP natively either way.
function mapError(e: unknown): string {
  if (e instanceof DirectGrantError && (e.status === 401 || e.keycloakError === "invalid_grant")) {
    if (e.keycloakDescription?.toLowerCase().includes("totp")) {
      return t.value.nativeLogin.otpRequired;
    }
    return t.value.nativeLogin.invalidCredentials;
  }
  return t.value.nativeLogin.genericError;
}

async function handleSubmit() {
  if (!canSubmit.value) return;
  isBusy.value = true;
  errorMessage.value = "";
  try {
    await loginWithCredentials(email.value.trim(), password.value);
    props.onSuccess();
  } catch (e) {
    errorMessage.value = mapError(e);
  } finally {
    isBusy.value = false;
  }
}

// Fallback to the existing PKCE/system-browser flow — kept for cases Direct Access Grant can't
// handle here (OTP) or if it ever breaks; identical success path (both end in applyTokens()).
async function handleBrowserFallback() {
  if (isBusy.value) return;
  isBusy.value = true;
  errorMessage.value = "";
  try {
    await login();
    props.onSuccess();
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : t.value.nativeLogin.genericError;
  } finally {
    isBusy.value = false;
  }
}
</script>

<style scoped>
/* Mirrors keycloak-data/themes/vomi-b2b/login/resources/css/login.css so this screen looks
   continuous with the Keycloak-hosted invite/login pages the user sees just before landing here. */
.vomi-login-page {
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem;
  background: linear-gradient(-45deg, #93a898, #7d9185, #748a7c, #889d90);
  background-size: 400% 400%;
  animation: vomi-gradient-shift 15s ease infinite;
}

@keyframes vomi-gradient-shift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.vomi-login-card {
  width: 100%;
  max-width: 24rem;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 28px;
  padding: 2.5rem 2rem;
  box-shadow:
    0 32px 64px -12px rgba(0, 0, 0, 0.14),
    0 16px 32px -8px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.3) inset;
}

.vomi-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

.vomi-logo-circle {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #889d90 0%, #556255 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(136, 157, 144, 0.3);
}

.vomi-logo-circle img {
  height: 2.25rem;
  width: auto;
  filter: brightness(0) invert(1);
}

.vomi-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin: 0 0 1.75rem;
}

.vomi-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.vomi-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.vomi-field label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
}

.vomi-error {
  display: block;
  font-size: 0.75rem;
  color: #ef4444;
}

.vomi-browser-fallback {
  margin-top: 1rem;
  width: 100%;
  text-align: center;
  font-size: 0.75rem;
  color: #6b7280;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  font-family: inherit;
}

.vomi-browser-fallback:disabled {
  opacity: 0.5;
}

/* PrimeVue's Button already picks up the Vomi primary color from VomiB2BPreset, but the Keycloak
   pages use a specific gradient/uppercase treatment — matched here via :deep() on the rendered
   .p-button root so it reads as the same button, not just "same-ish green". */
.vomi-submit :deep(.p-button) {
  background: linear-gradient(135deg, #889d90 0%, #556255 50%, #6b7b6b 100%);
  background-size: 200% 100%;
  border: none;
  border-radius: 14px;
  padding: 14px 28px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  box-shadow:
    0 4px 14px rgba(136, 157, 144, 0.3),
    0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.vomi-submit :deep(.p-button:not(:disabled):hover) {
  background-position: 100% 0;
  transform: translateY(-2px);
  box-shadow:
    0 8px 24px rgba(136, 157, 144, 0.4),
    0 4px 8px rgba(0, 0, 0, 0.12);
}
</style>
